"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { applyGamTargeting, getGamTargeting } from "@/components/gam-targeting";

declare global {
  interface Window {
    googletag: any;
    __gamTargetingPath?: string;
  }
}

/** Keeps GAM page-level targeting current during client-side navigation. */
export default function GamPageTargeting() {
  const pathname = usePathname();

  useEffect(() => {
    let frameId: number | undefined;

    const applyCurrentTargeting = () => {
      window.googletag = window.googletag || { cmd: [] };
      window.googletag.cmd.push(() => {
        // Read metadata at execution time rather than capturing values from
        // the previous route while Next.js is updating the document head.
        applyGamTargeting(window.googletag.pubads(), getGamTargeting(pathname));
        window.__gamTargetingPath = pathname;
        window.dispatchEvent(new CustomEvent<string>("gam-targeting-ready", { detail: pathname }));
      });
    };

    const scheduleTargetingUpdate = () => {
      if (frameId) cancelAnimationFrame(frameId);
      // Let the route commit update <head>; the observer below also catches
      // metadata that arrives after this first frame.
      frameId = requestAnimationFrame(applyCurrentTargeting);
    };

    const observer = new MutationObserver(scheduleTargetingUpdate);
    observer.observe(document.head, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["content", "name"],
    });

    scheduleTargetingUpdate();

    return () => {
      observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [pathname]);

  return null;
}
